import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './ImportAgentDialog.scss';
import {
  AlertToastType,
  ButtonFlavor, ButtonSize, ButtonType, ElementSize, ExButton,
  ExFileUploader,
  ExIcon,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  SizeUnit,
  ToastController,
} from '@boomi/exosphere';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import { sanitizeHtml } from '@common/dompurify';
import pendoHelper from '@utils/data/pendoHelper';
import {
  generateUniqueKey,
  handleError,
  importAgent,
  importPreviewForAgent,
} from '@services/agent-foundation/agent-foundation-api-service';
import AgentPreview from '@components/AgentPreview/AgentPreview';

function ImportAgentDialog({
  goBack, setBuilderDialogTitle, setShowDialogIcon, onCancel, showDialogIcon, setDialogIcon,
}: any) {
  const [t] = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [importPreviewDetails, setImportPreviewDetails] = useState<any>({});
  const [showPreview, setShowPreview] = useState(false);
  const [showImportError, setShowImportError] = useState(false);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  // const [selectedBuilderStep, setSelectedBuilderStep] = useState('Profile');
  const instructionsArr = [
    t('import_agent_instruction_1'),
    t('import_agent_instruction_2'),
  ];
  const {
    wizardData,
    updateWizardStep,
    createAgentMethod,
    setCreateAgentMethod,
  } = useBoomiAgentFoundationContext();
  const handleFileUpload = async (e: CustomEvent) => {
    const file = e.detail.value[e.detail.value.length - 1];
    if (!file) {
      setUploadedFile(null);
      return;
    }
    setTimeout(() => {
      setUploadedFile(file);
    }, 3000);
  };
  const validateUploadedFile = async () => {
    if (uploadedFile) {
      try {
        const formData = new FormData();
        formData.append('import_config', uploadedFile);
        setIsLoading(true);
        const response: any = await importPreviewForAgent(formData);
        if (!response?.success || response?.data?.agents?.length === 0) {
          // handleError(response, 'Failed to generate agent import preview');
          setShowPreview(false);
          setShowDialogIcon(true);
          setShowImportError(true);
          setBuilderDialogTitle(t('import_agent_error_dialog_title'));
          setImportErrors(response?.errors || [{ message: response?.error }]);
          if (response?.data?.agents?.length === 0 && !response?.errors && !response?.error) {
            setImportErrors([{ message: 'No agents found in the file' }]);
          }
          setDialogIcon('error');
          setIsLoading(false);
          return;
        }
        setImportPreviewDetails(response?.data);
        setShowPreview(true);
        setShowImportError(false);
        setShowDialogIcon(true);
        setDialogIcon('Check');
        setIsLoading(false);
        setBuilderDialogTitle(t('import_agent_valid_dialog_title'));
      } catch (error) {
        setIsLoading(false);
        console.error('Error uploading file:', error);
      }
    }
  };
  const importAgentAndCloseDialog = async () => {
    if (uploadedFile) {
      try {
        const formData = new FormData();
        formData.append('import_config', uploadedFile);
        setIsLoading(true);
        const response: any = await importAgent(formData);
        if (response?.success) {
          pendoHelper.trackEvent('agentGarden-createAgent', {
            savedFrom: t('import_agent_import_and_close_button'),
            agentStatus: 'Success',
            agentId: response?.data?.agents[0]?.id,
            agentName: importPreviewDetails.agents[0]?.name,
            createMethod: createAgentMethod,
          });
        } else {
          pendoHelper.trackEvent('agentGarden-createAgent', {
            savedFrom: t('import_agent_import_and_close_button'),
            agentStatus: 'Error',
            agentName: importPreviewDetails.agents[0]?.name,
          });
        }
        if (!response?.success) {
          handleError(response, 'Failed to import agent');
          setShowPreview(false);
          setIsLoading(false);
          return;
        }
        ToastController.show({
          description: t('import_agent_success_message'),
          type: AlertToastType.SUCCESS,
        });
        setShowDialogIcon(false);
        setIsLoading(false);
        onCancel();
        navigate('/agents');
      } catch (error) {
        setIsLoading(false);
        console.error('Error uploading file:', error);
      }
    }
  };
  const importAgentAndEdit = async () => {
    if (uploadedFile) {
      try {
        const formData = new FormData();
        formData.append('import_config', uploadedFile);
        const response: any = await importAgent(formData);
        if (response?.success) {
          pendoHelper.trackEvent('agentGarden-createAgent', {
            savedFrom: t('import_agent_dialog_import_and_edit_btn'),
            agentStatus: 'Success',
            agentId: response?.data?.agents[0]?.id,
            agentName: importPreviewDetails.agents[0]?.name,
            createMethod: createAgentMethod,
          });
        } else {
          pendoHelper.trackEvent('agentGarden-createAgent', {
            savedFrom: t('import_agent_dialog_import_and_edit_btn'),
            agentStatus: 'Error',
            agentName: importPreviewDetails.agents[0]?.name,
          });
        }
        if (!response?.success) {
          handleError(response, 'Failed to import agent');
          setShowPreview(false);
          return;
        }
        const id = response?.data?.agents[0];
        updateWizardStep('profile', {
          ...wizardData.profile.data,
          id,
        });
        ToastController.show({
          description: t('import_agent_success_message'),
          type: AlertToastType.SUCCESS,
        });
        setShowDialogIcon(false);
        onCancel();
        navigate('/agent-designer');
      } catch (error) {
        console.error('Error uploading file:', error);
        console.error('Error in pendo tracking:', error);
      }
    }
  };
  return (
    <>
      <div className={
        showDialogIcon
          ? 'import-agent-dialog-container import-agent-custom-height'
          : 'import-agent-dialog-container'
      }
      >
        {(isLoading) && (
          <div className="import-agent-loader">
            <ExLoader
              variant={LoaderVariant.SPINNER}
              label="Loading..."
              size={LoaderSize.MEDIUM}
              inline
            />
          </div>
        )}
        {!isLoading && showPreview && (
          <AgentPreview
            importPreviewDetails={importPreviewDetails.agents[0]}
            sources={importPreviewDetails.sources}
          />
        )}
        {!isLoading && showImportError && (
          <div>
            <div className="import-error-title">
              {Array.isArray(importErrors) ? importErrors.length : 0}
              {' '}
              {Array.isArray(importErrors) && importErrors.length === 1 ? 'Error' : 'Errors'}
              {' '}
              {t('found')}
            </div>
            <div>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {Array.isArray(importErrors) && importErrors.map((err: any, idx: number) => (
                  <li key={generateUniqueKey(idx)} style={{ color: 'var(--exo-color-status-error)' }}>
                    {err.message}
                    {err.line_number !== undefined && err.line_number !== null && (
                      <>
                        {' '}
                        (
                        {t('line')}
                        {' '}
                        {err.line_number}
                        )
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {!isLoading && !showPreview && !showImportError && (
          <div className="import-dialog-container">
            <span className="import-title">{t('instructions')}</span>
            <div className="import-instructions-list">
              {instructionsArr.map((instruction, index) => (
                <div key={generateUniqueKey(index)} className="instruction-item">
                  <ExIcon
                    icon="Check"
                    hide-browser-tooltip=""
                    className="check-green-icon"
                  />
                  {instruction}
                </div>
              ))}
            </div>
            <div className="help-container">
              {t('help')}
              {' '}
              <span
                className="import_help_link"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(t('import_agent_help')),
                }}
              />
            </div>
            <span className="import-title">{t('agent_file')}</span>
            <ExFileUploader
              size={ElementSize.LARGE}
              icon
              maxSize={2}
              maxSizeUnit={SizeUnit.MB}
              fileTypes="application/x-yaml,text/yaml,application/yaml,.yaml,.yml"
              onChange={handleFileUpload}
              className="import-file-uploader"
              buttonText="Select file..."
              data-testid="import-agent-file-uploader"
              helpText="Only YAML files. File size 2MB max."
              singleFileUpload
              allowUndetectedMimeTypes
            />
          </div>
        )}
      </div>
      <div className="import-agent-footer" slot="footer">
        <ExButton
          data-testid="import_agent_dialog_back_button"
          flavor={ButtonFlavor.BRANDED}
          type={ButtonType.SECONDARY}
          size={ButtonSize.DEFAULT}
          className="ai-back-button"
          onClick={() => {
            if (!showPreview && !showImportError) {
              goBack();
            } else {
              setShowPreview(false);
              setShowImportError(false);
              setUploadedFile(null);
              setShowDialogIcon(false);
              setDialogIcon('');
              setBuilderDialogTitle(t('import_agent_dialog_title'));
            }
          }}
          slot="footer"
        >
          {t('import_agent_back_button')}
        </ExButton>
        {(showPreview || showImportError) && (
          <div className="import-agent-button-container">
            <ExButton
              flavor={ButtonFlavor.BRANDED}
              type={ButtonType.SECONDARY}
              size={ButtonSize.DEFAULT}
              className="ai-build-button"
              data-testid="import_agent_dialog_import_and_close_btn"
              onClick={() => {
                setCreateAgentMethod('Import');
                importAgentAndCloseDialog();
              }}
              slot="footer"
              disabled={showImportError}
            >
              {t('import_agent_import_and_close_button')}
            </ExButton>
            <ExButton
              flavor={ButtonFlavor.BRANDED}
              type={ButtonType.PRIMARY}
              size={ButtonSize.DEFAULT}
              className="ai-build-button"
              data-testid="import_agent_dialog_import_and_edit_btn"
              slot="footer"
              disabled={!uploadedFile || showImportError}
              onClick={() => {
                setCreateAgentMethod('Import');
                importAgentAndEdit();
              }}
            >
              {t('import_agent_import_and_edit_button')}
            </ExButton>
          </div>
        )}
        {!showPreview && !showImportError && (
          <ExButton
            flavor={ButtonFlavor.BRANDED}
            type={ButtonType.PRIMARY}
            size={ButtonSize.DEFAULT}
            className="ai-build-button"
            data-testid="import_agent_dialog_save_and_continue_btn"
            onClick={() => {
              validateUploadedFile();
            }}
            slot="footer"
            disabled={!uploadedFile}
          >
            {t('import_agent_save_button')}
          </ExButton>
        )}
      </div>
    </>
  );
}

export default ImportAgentDialog;
